import React, { useState, useRef, useCallback } from 'react';
import { API_URL } from '../config';
import axios from 'axios';

const FilePrintPage = () => {
  // Состояния для файлов
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);

  // Состояния для печати
  const [copies, setCopies] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Состояние для предпросмотра
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Настройки позиционирования
  const [positioning, setPositioning] = useState({
    horizontalAlign: 'center', // left, center, right
    verticalAlign: 'center',   // top, center, bottom
    scale: 100,                // масштаб в процентах
    fitToPage: true,           // вписать в страницу
    marginTop: 10,
    marginBottom: 10,
    marginLeft: 10,
    marginRight: 10
  });

  const fileInputRef = useRef(null);

  // Обработка drag & drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(Array.from(e.dataTransfer.files));
    }
  }, []);

  // Выбор файлов через input
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelected(Array.from(e.target.files));
    }
  };

  // Обработка выбранных файлов
  const handleFilesSelected = (files) => {
    const validExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'bmp', 'tiff', 'tif'];
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return validExtensions.includes(ext);
    });

    if (validFiles.length !== files.length) {
      alert('Некоторые файлы были пропущены. Поддерживаются: PDF, JPG, PNG, BMP, TIFF');
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      // Сбрасываем загруженные файлы при добавлении новых
      setUploadedFiles([]);
    }
  };

  // Удаление файла из списка
  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });

    // Закрываем предпросмотр если удаляем этот файл
    if (previewFile === selectedFiles[index]) {
      closePreview();
    }
  };

  // Очистка всех файлов
  const clearAllFiles = () => {
    setSelectedFiles([]);
    setUploadedFiles([]);
    setUploadProgress({});
    closePreview();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Предпросмотр файла
  const openPreview = (file, index) => {
    // Закрываем предыдущий предпросмотр
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  // Закрыть предпросмотр
  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  // Загрузка файлов на сервер
  const uploadFiles = async () => {
    const token = localStorage.getItem('token');
    const uploaded = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];

      // Пропускаем уже загруженные
      if (uploadedFiles[i]) {
        uploaded.push(uploadedFiles[i]);
        continue;
      }

      try {
        setUploadProgress(prev => ({ ...prev, [i]: 0 }));

        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API_URL}/api/upload-file`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [i]: percent }));
          }
        });

        if (response.data.fileId || response.data.filename) {
          uploaded.push({
            fileId: response.data.fileId,
            filename: response.data.filename,
            fileUrl: response.data.fileUrl || `${API_URL}/api/files/${response.data.filename}`,
            fileName: file.name
          });
          setUploadProgress(prev => ({ ...prev, [i]: 100 }));
        } else {
          throw new Error('Сервер не вернул данные файла');
        }
      } catch (err) {
        console.error(`Ошибка загрузки файла ${file.name}:`, err);
        setUploadProgress(prev => ({ ...prev, [i]: -1 })); // -1 = ошибка
        throw new Error(`Ошибка загрузки файла ${file.name}: ${err.message}`);
      }
    }

    setUploadedFiles(uploaded);
    return uploaded;
  };

  // Отправка на печать (загрузка + печать одной кнопкой)
  const handlePrint = async () => {
    if (selectedFiles.length === 0) {
      alert('Выберите файлы для печати');
      return;
    }

    try {
      setPrinting(true);

      // Сначала загружаем файлы если они еще не загружены
      let filesToPrint = uploadedFiles;
      if (uploadedFiles.length !== selectedFiles.length) {
        setUploading(true);
        filesToPrint = await uploadFiles();
        setUploading(false);
      }

      const token = localStorage.getItem('token');
      let successCount = 0;
      let errorMessages = [];

      // Отправляем каждый файл на печать
      for (const file of filesToPrint) {
        try {
          const response = await axios.post(`${API_URL}/api/print-files`, {
            fileUrl: file.fileUrl,
            filename: file.filename,
            fileId: file.fileId,
            copies: copies,
            positioning: positioning
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            timeout: 60000
          });

          if (response.data.success) {
            successCount++;
          } else {
            errorMessages.push(`${file.fileName}: ${response.data.error || 'Неизвестная ошибка'}`);
          }
        } catch (err) {
          errorMessages.push(`${file.fileName}: ${err.response?.data?.error || err.message}`);
        }
      }

      if (successCount > 0) {
        alert(`Отправлено на печать: ${successCount} файл(ов), ${copies} копий каждый`);
      }

      if (errorMessages.length > 0) {
        alert('Ошибки печати:\n' + errorMessages.join('\n'));
      }

      // Очищаем после успешной печати всех файлов
      if (errorMessages.length === 0) {
        clearAllFiles();
      }

    } catch (err) {
      console.error('Ошибка печати:', err);
      alert(`Ошибка печати: ${err.message}`);
    } finally {
      setPrinting(false);
      setUploading(false);
    }
  };

  // Форматирование размера файла
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Получить иконку по типу файла
  const getFileIcon = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    if (ext === 'pdf') {
      return (
        <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      );
    }
    return (
      <svg className="h-8 w-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
      </svg>
    );
  };

  // Определить тип файла для предпросмотра
  const isImageFile = (file) => {
    const ext = file.name.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'bmp', 'tiff', 'tif'].includes(ext);
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Печать файлов</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Левая колонка - загрузка и список файлов */}
          <div className="space-y-4">
            {/* Область загрузки файлов */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                dragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="space-y-3">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Перетащите файлы сюда
                  </p>
                  <p className="text-sm text-gray-500">или нажмите для выбора</p>
                </div>

                <p className="text-xs text-gray-400">
                  PDF, JPG, PNG, BMP, TIFF (до 50MB)
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileInputChange}
                  accept=".pdf,.jpg,.jpeg,.png,.bmp,.tiff,.tif"
                  multiple
                />
              </div>
            </div>

            {/* Список выбранных файлов */}
            {selectedFiles.length > 0 && (
              <div className="border rounded-lg">
                <div className="flex justify-between items-center px-4 py-2 bg-gray-50 border-b">
                  <span className="font-medium text-gray-700">
                    Выбрано файлов: {selectedFiles.length}
                  </span>
                  <button
                    onClick={clearAllFiles}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Очистить все
                  </button>
                </div>

                <div className="max-h-64 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between px-4 py-3 border-b last:border-b-0 hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {getFileIcon(file.name)}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(file.size)}
                          </p>
                          {/* Прогресс загрузки */}
                          {uploadProgress[index] !== undefined && uploadProgress[index] >= 0 && uploadProgress[index] < 100 && (
                            <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                              <div
                                className="bg-blue-600 h-1.5 rounded-full transition-all"
                                style={{ width: `${uploadProgress[index]}%` }}
                              />
                            </div>
                          )}
                          {uploadProgress[index] === 100 && (
                            <span className="text-xs text-green-600">Загружен</span>
                          )}
                          {uploadProgress[index] === -1 && (
                            <span className="text-xs text-red-600">Ошибка загрузки</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-2">
                        {/* Кнопка предпросмотра */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openPreview(file, index);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Предпросмотр"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        {/* Кнопка удаления */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(index);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Удалить"
                        >
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Правая колонка - настройки печати */}
          <div className="space-y-4">
            {/* Количество копий */}
            <div className="border rounded-sm p-2">
              <h2 className="font-small text-gray-800 mb-2">Количество копий</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCopies(prev => Math.max(1, prev - 1))}
                  className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-xs font-small"
                >
                  -
                </button>
                <input
                  type="number"
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))}
                  className="w-10 h-5 text-center text-xs font-small border rounded-lg py-2"
                  min="1"
                  max="99"
                />
                <button
                  onClick={() => setCopies(prev => Math.min(99, prev + 1))}
                  className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-xs font-small"
                >
                  +
                </button>
              </div>

              


            </div>

            {/* Вписать в страницу */}
            <div className="mb-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={positioning.fitToPage}
                    onChange={(e) => setPositioning(prev => ({ ...prev, fitToPage: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Вписать в страницу</span>
                </label>
              </div>

            
          </div>
        </div>

        {/* Кнопка печати */}
        <div className="mt-6">
          <button
            onClick={handlePrint}
            disabled={selectedFiles.length === 0 || uploading || printing}
            className={`w-full py-4 px-6 rounded-lg font-medium text-lg transition-colors ${
              selectedFiles.length === 0 || uploading || printing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {uploading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Загрузка файлов...
              </span>
            ) : printing ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Отправка на печать...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Печать ({selectedFiles.length} {selectedFiles.length === 1 ? 'файл' : 'файлов'} x {copies} {copies === 1 ? 'копия' : 'копий'})
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Модальное окно предпросмотра */}
      {previewFile && previewUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Заголовок */}
            <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
              <h3 className="font-medium text-gray-800 truncate">{previewFile.name}</h3>
              <button
                onClick={closePreview}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Контент предпросмотра */}
            <div className="overflow-auto max-h-[calc(90vh-60px)] p-4 bg-gray-100">
              {isImageFile(previewFile) ? (
                <img
                  src={previewUrl}
                  alt={previewFile.name}
                  className="max-w-full h-auto mx-auto"
                  style={{ maxHeight: 'calc(90vh - 100px)' }}
                />
              ) : (
                <iframe
                  src={previewUrl}
                  title={previewFile.name}
                  className="w-full bg-white"
                  style={{ height: 'calc(90vh - 100px)' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilePrintPage;
